/**
 * useCanvases — list the user's Knowledge Canvases.
 *
 * v1 ref: ``ando-frontend/app/hooks/useCanvases.ts`` (consumed by
 * ``CanvasesHubPage.tsx``).
 *
 * Polling cadence: 30s (matches v1 + v2 documents hub) so a compile that
 * just kicked off transitions from ``compiling`` → ``ready`` in the UI
 * without a manual refresh. The WS event ``canvas_section_complete`` will
 * replace this poll in F-LATER; until the WS bridge ships canvas events,
 * 30s polling is the canonical pattern.
 *
 * Capability gating happens at the route boundary (``canvases.tsx``):
 * ``useHasCapability("canvases")`` toggles between the gated view and this
 * hook's render path. The hook itself accepts ``enabled`` so callers can
 * short-circuit before the query fires (no 403 flash on first paint).
 */

import { useQuery } from "@tanstack/react-query";
import {
  listCanvases,
  type Canvas,
  type CanvasListParams,
} from "@ando/core";

export function canvasesQueryKey(
  params: CanvasListParams,
): readonly unknown[] {
  return [
    "canvases",
    params.status ?? "active",
    params.scope ?? null,
    params.entity_type ?? null,
    params.search ?? null,
    params.limit ?? null,
    params.offset ?? null,
  ] as const;
}

export interface UseCanvasesOptions {
  /** Disable the query while caller-side preconditions are still resolving. */
  enabled?: boolean;
  params?: CanvasListParams;
}

export interface UseCanvasesResult {
  readonly canvases: readonly Canvas[];
  readonly total: number;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly refetch: () => Promise<unknown>;
}

const DEFAULT_PARAMS: CanvasListParams = { status: "active", limit: 50 };

export function useCanvases(options: UseCanvasesOptions = {}): UseCanvasesResult {
  const params = { ...DEFAULT_PARAMS, ...(options.params ?? {}) };
  const enabled = options.enabled ?? true;

  const query = useQuery({
    queryKey: canvasesQueryKey(params),
    queryFn: () => listCanvases(params),
    enabled,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  return {
    canvases: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => query.refetch(),
  };
}
